import React, { FormEvent, useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { listPracticePapers } from "../../../../api/studentPracticePapers"
import LearnerField from "../../../../component/student/LearnerField"
import LearnerSelect from "../../../../component/student/LearnerSelect"
import { getLearnerApiErrorMessage } from "../../../../auth/learner/errors"
import LearnerModal from "../../components/LearnerModal"
import useStudentSubjects from "../../hooks/useStudentSubjects"
import {
  paperDisplayTitle,
  type PaperSession,
} from "../../PracticePapers/types"
import type { NewDashboardGoalInput } from "../types"
import RestartPapersField from "./RestartPapersField"
import "./AddGoalModal.style.scss"

interface AddGoalModalProps {
  isOpen: boolean
  isSaving?: boolean
  onClose: () => void
  onSubmit: (goal: NewDashboardGoalInput) => Promise<void> | void
}

type FormErrors = Partial<Record<"subject" | "title" | "papers" | "dueDate", string>>

const emptyForm = {
  subjectId: "",
  title: "",
  dueDate: "",
}

const MAX_PAPERS_PER_GOAL = 10
const GOAL_PAPER_PAGE_SIZE = 100

const selectableStatuses = new Set([
  "not_started",
  "in_progress",
  "submitted",
  "marked",
  "abandoned",
])

const startedStatuses = new Set([
  "in_progress",
  "submitted",
  "marked",
  "abandoned",
])

const paperLabel = (paper: PaperSession) => {
  const count = paper.questions?.length || paper.questionCount || 0
  const status = (paper.status || "not_started").replace("_", " ")
  return `${paperDisplayTitle(paper)} · ${count} q · ${status}`
}

const localDateInputValue = (value: Date) => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, "0")
  const day = String(value.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const AddGoalModal = ({
  isOpen,
  isSaving = false,
  onClose,
  onSubmit,
}: AddGoalModalProps) => {
  const { subjects, subjectsLoading, subjectsError } = useStudentSubjects()
  const [form, setForm] = useState(emptyForm)
  const [paperIds, setPaperIds] = useState<string[]>([])
  const [restartPapers, setRestartPapers] = useState(false)
  const [papers, setPapers] = useState<PaperSession[]>([])
  const [papersLoading, setPapersLoading] = useState(false)
  const [papersError, setPapersError] = useState<string | null>(null)
  const [errors, setErrors] = useState<FormErrors>({})
  const minDueDate = useMemo(() => localDateInputValue(new Date()), [])

  const subjectOptions = useMemo(
    () =>
      subjects.map((subject) => ({
        value: subject.id,
        label: subject.title,
      })),
    [subjects]
  )

  const subjectPapers = useMemo(
    () => papers.filter((paper) => selectableStatuses.has(paper.status)),
    [papers]
  )

  const startedSelectedCount = useMemo(
    () =>
      subjectPapers.filter(
        (paper) =>
          paperIds.includes(paper.id) && startedStatuses.has(paper.status)
      ).length,
    [paperIds, subjectPapers]
  )

  useEffect(() => {
    if (startedSelectedCount === 0) setRestartPapers(false)
  }, [startedSelectedCount])

  useEffect(() => {
    if (!isOpen) return
    setForm(emptyForm)
    setPaperIds([])
    setRestartPapers(false)
    setPapers([])
    setPapersError(null)
    setErrors({})
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !form.subjectId) {
      setPapers([])
      setPapersError(null)
      setPapersLoading(false)
      return
    }

    let cancelled = false
    setPapersLoading(true)
    setPapersError(null)
    listPracticePapers(1, GOAL_PAPER_PAGE_SIZE, form.subjectId)
      .then((res) => {
        if (cancelled) return
        if (!res.data?.success || !Array.isArray(res.data.data)) {
          throw new Error(res.data?.message || "Could not load practice papers")
        }
        setPapers(res.data.data)
      })
      .catch((err) => {
        if (cancelled) return
        setPapers([])
        setPapersError(
          getLearnerApiErrorMessage(err, "Could not load practice papers")
        )
      })
      .finally(() => {
        if (!cancelled) setPapersLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, form.subjectId])

  const setField = (name: keyof typeof emptyForm, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }))
    if (name === "subjectId") {
      setPaperIds([])
      setRestartPapers(false)
      setErrors((prev) => ({ ...prev, subject: undefined, papers: undefined }))
      return
    }
    setErrors((prev) => ({ ...prev, [name]: undefined }))
  }

  const togglePaper = (paperId: string) => {
    setPaperIds((prev) => {
      if (prev.includes(paperId)) {
        return prev.filter((id) => id !== paperId)
      }
      if (prev.length >= MAX_PAPERS_PER_GOAL) return prev
      return [...prev, paperId]
    })
    setErrors((prev) => ({ ...prev, papers: undefined }))
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const selected = subjects.find((s) => s.id === form.subjectId)
    const title = form.title.trim()
    const dueRaw = form.dueDate.trim()
    const nextErrors: FormErrors = {}

    if (!selected) nextErrors.subject = "Select a subject"
    if (!title) nextErrors.title = "Describe the goal"
    if (!dueRaw) nextErrors.dueDate = "Pick a due date"
    else if (dueRaw < minDueDate) nextErrors.dueDate = "Due date cannot be in the past"
    if (paperIds.length === 0) nextErrors.papers = "Select at least one paper"

    if (Object.keys(nextErrors).length > 0 || !selected) {
      setErrors(nextErrors)
      return
    }

    try {
      await onSubmit({
        subjectId: selected.id,
        title,
        paperIds,
        dueDate: dueRaw,
        restartPapers: startedSelectedCount > 0 && restartPapers,
      })
      onClose()
    } catch (err) {
      toast.error(getLearnerApiErrorMessage(err, "Could not save goal"))
    }
  }

  const busy = isSaving || subjectsLoading

  return (
    <LearnerModal
      isOpen={isOpen}
      title="Add revision goal"
      description="Pick practice papers for a subject. Progress updates as you sit and submit them."
      dialogClassName="is-form"
      isBusy={busy}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="is-ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-goal-form"
            className="is-primary"
            disabled={busy || !!subjectsError}
          >
            {isSaving ? "Saving…" : "Save goal"}
          </button>
        </>
      }
    >
      <form
        id="add-goal-form"
        className="add-goal-modal"
        onSubmit={(event) => {
          void handleSubmit(event)
        }}
        noValidate
      >
        <LearnerSelect
          id="goal-subject"
          label="Subject"
          name="subject"
          value={form.subjectId}
          onChange={(e) => setField("subjectId", e.target.value)}
          options={subjectOptions}
          placeholder={
            subjectsLoading ? "Loading subjects…" : "Select a subject"
          }
          error={errors.subject || subjectsError || undefined}
          disabled={busy || !!subjectsError || subjectOptions.length === 0}
          autoFocus
        />
        <LearnerField
          id="goal-title"
          label="Goal"
          name="title"
          value={form.title}
          onChange={(e) => setField("title", e.target.value)}
          placeholder="e.g. Finish genetics papers before Friday"
          error={errors.title}
          disabled={busy}
        />
        <fieldset className="add-goal-modal__papers">
          <legend>Practice papers</legend>
          {!form.subjectId ? (
            <p>Select a subject to see its papers.</p>
          ) : papersLoading ? (
            <p>Loading papers…</p>
          ) : papersError ? (
            <p className="add-goal-modal__error">{papersError}</p>
          ) : subjectPapers.length === 0 ? (
            <p>No practice papers for this subject yet. Generate one first.</p>
          ) : (
            subjectPapers.map((paper) => {
              const checked = paperIds.includes(paper.id)
              const atLimit =
                !checked && paperIds.length >= MAX_PAPERS_PER_GOAL
              return (
                <label key={paper.id} className="add-goal-modal__paper">
                  <input
                    type="checkbox"
                    name="paperIds"
                    value={paper.id}
                    checked={checked}
                    onChange={() => togglePaper(paper.id)}
                    disabled={busy || atLimit}
                  />
                  <span>{paperLabel(paper)}</span>
                </label>
              )
            })
          )}
          {paperIds.length > 0 ? (
            <p>
              {paperIds.length} of {MAX_PAPERS_PER_GOAL} selected
            </p>
          ) : null}
          {errors.papers ? (
            <p className="add-goal-modal__error">{errors.papers}</p>
          ) : null}
        </fieldset>
        {startedSelectedCount > 0 ? (
          <RestartPapersField
            checked={restartPapers}
            paperCount={startedSelectedCount}
            disabled={busy}
            onChange={setRestartPapers}
          />
        ) : null}
        <LearnerField
          id="goal-due"
          label="Due date"
          name="dueDate"
          type="date"
          value={form.dueDate}
          onChange={(e) => setField("dueDate", e.target.value)}
          error={errors.dueDate}
          disabled={busy}
          min={minDueDate}
        />
      </form>
    </LearnerModal>
  )
}

export default AddGoalModal
